import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Megaphone, Plus, Edit2, Trash2, Target, Gift, 
  Calendar, Clock, Zap, MoreVertical, X, Eye, Check
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import UpgradePlanModal from '../components/UpgradePlanModal';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CampaignsPage = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    goal: 10,
    reward: '',
    reward_description: '',
    active: true,
    location_id: '',
    double_stamp_months: [],
    double_stamp_days: [],
    double_stamp_hours: [],
    rate_limit_hours: 8
  });

  const months = [
    { value: 1, label: 'Ene' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
    { value: 4, label: 'Abr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' }, { value: 8, label: 'Ago' }, { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dic' }
  ];

  const days = [
    { value: 0, label: 'Lun' }, { value: 1, label: 'Mar' }, { value: 2, label: 'Mié' },
    { value: 3, label: 'Jue' }, { value: 4, label: 'Vie' }, { value: 5, label: 'Sáb' },
    { value: 6, label: 'Dom' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar campañas');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Error al cargar campañas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign.id}`, formData);
        toast.success('Campaña actualizada');
      } else {
        await axios.post(`${API}/campaigns`, formData);
        toast.success('Campaña creada');
      }
      setDialogOpen(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
      // Check if it's a plan limit error
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response?.data?.detail || 'Has alcanzado el límite de campañas de tu plan actual.');
        setUpgradeModalOpen(true);
        setDialogOpen(false);
      } else {
        toast.error(error.response?.data?.detail || 'Error al guardar');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta campaña?')) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      toast.success('Campaña eliminada');
      fetchCampaigns();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      goal: campaign.goal,
      reward: campaign.reward,
      reward_description: campaign.reward_description || '',
      active: campaign.active,
      location_id: campaign.location_id || '',
      double_stamp_months: campaign.double_stamp_months || [],
      double_stamp_days: campaign.double_stamp_days || [],
      double_stamp_hours: campaign.double_stamp_hours || [],
      rate_limit_hours: campaign.rate_limit_hours || 8
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCampaign(null);
    setFormData({
      name: '',
      goal: 10,
      reward: '',
      reward_description: '',
      active: true,
      location_id: '',
      double_stamp_months: [],
      double_stamp_days: [],
      double_stamp_hours: [],
      rate_limit_hours: 8
    });
  };

  const toggleMonth = (month) => {
    setFormData(prev => ({
      ...prev,
      double_stamp_months: prev.double_stamp_months.includes(month)
        ? prev.double_stamp_months.filter(m => m !== month)
        : [...prev.double_stamp_months, month]
    }));
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      double_stamp_days: prev.double_stamp_days.includes(day)
        ? prev.double_stamp_days.filter(d => d !== day)
        : [...prev.double_stamp_days, day]
    }));
  };

  // Check if user can use double stamp feature
  const canUseDoubleStamp = user?.plan === 'growth' || user?.plan === 'scale';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Campañas</h1>
          <p className="text-slate-600">Gestiona tus programas de fidelización</p>
        </div>
        <Button 
          className="btn-primary"
          onClick={() => { resetForm(); setDialogOpen(true); }}
          data-testid="create-campaign-btn"
        >
          <Plus className="w-5 h-5" /> Nueva campaña
        </Button>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <motion.div 
          className="card-dashboard text-center py-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Megaphone className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Sin campañas todavía</h3>
          <p className="text-slate-600 mb-6">Crea tu primera campaña para empezar a fidelizar clientes</p>
          <Button 
            className="btn-primary"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="w-5 h-5" /> Crear campaña
          </Button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((campaign, i) => (
            <motion.div
              key={campaign.id}
              className="card-dashboard group hover:shadow-card-hover transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`campaign-card-${campaign.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${campaign.active ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400'}`}>
                    <Megaphone className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{campaign.name}</h3>
                    <Badge variant={campaign.active ? "default" : "secondary"} className="mt-1">
                      {campaign.active ? 'Activa' : 'Pausada'}
                    </Badge>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setPreviewCampaign(campaign)}>
                      <Eye className="w-4 h-4 mr-2" /> Ver voucher
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEditDialog(campaign)}>
                      <Edit2 className="w-4 h-4 mr-2" /> Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => handleDelete(campaign.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Target className="w-4 h-4 text-primary" />
                  <span>Meta: {campaign.goal} sellos</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Gift className="w-4 h-4 text-secondary" />
                  <span className="truncate">{campaign.reward}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>Límite: {campaign.rate_limit_hours}h entre sellos</span>
                </div>
                {(campaign.double_stamp_months?.length > 0 || campaign.double_stamp_days?.length > 0) && (
                  <div className="flex items-center gap-2 text-sm">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span className="text-amber-600 font-medium">Doble sello configurado</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Editar campaña' : 'Nueva campaña'}
            </DialogTitle>
            <DialogDescription>
              Configura los detalles de tu programa de sellos
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label>Nombre de la campaña</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Café del mes"
                required
                data-testid="campaign-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sellos necesarios</Label>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: parseInt(e.target.value) })}
                  required
                  data-testid="campaign-goal-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Horas entre sellos (1-12)</Label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={formData.rate_limit_hours}
                  onChange={(e) => setFormData({ ...formData, rate_limit_hours: Math.min(12, Math.max(1, parseInt(e.target.value) || 1)) })}
                  required
                  data-testid="campaign-rate-limit-input"
                />
                <p className="text-xs text-slate-500">Tiempo mínimo entre cada sellado</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Premio</Label>
              <Input
                value={formData.reward}
                onChange={(e) => setFormData({ ...formData, reward: e.target.value })}
                placeholder="Ej: Café gratis"
                required
                data-testid="campaign-reward-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Descripción del premio (opcional)</Label>
              <textarea
                value={formData.reward_description}
                onChange={(e) => setFormData({ ...formData, reward_description: e.target.value })}
                placeholder="Ej: Café con leche tamaño grande con tostada de pan artesano y mermelada casera"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                rows={3}
                data-testid="campaign-reward-description-input"
              />
              <p className="text-xs text-slate-500">Describe con detalle el premio que recibirá el cliente</p>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-slate-900">Campaña activa</p>
                <p className="text-sm text-slate-500">Los clientes pueden acumular sellos</p>
              </div>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                data-testid="campaign-active-switch"
              />
            </div>

            {/* Double Stamp Config */}
            <div className={`space-y-4 p-4 rounded-xl border ${canUseDoubleStamp ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className={`w-5 h-5 ${canUseDoubleStamp ? 'text-amber-600' : 'text-slate-400'}`} />
                  <h4 className={`font-semibold ${canUseDoubleStamp ? 'text-amber-900' : 'text-slate-500'}`}>Doble Sello</h4>
                </div>
                {!canUseDoubleStamp && (
                  <Badge variant="secondary" className="text-xs">Plan Growth+</Badge>
                )}
              </div>

              {canUseDoubleStamp ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm text-amber-800">Meses con doble sello</Label>
                    <div className="flex flex-wrap gap-1">
                      {months.map(month => (
                        <button
                          key={month.value}
                          type="button"
                          onClick={() => toggleMonth(month.value)}
                          className={`px-2 py-1 text-xs rounded-lg transition-all ${
                            formData.double_stamp_months.includes(month.value)
                              ? 'bg-amber-600 text-white'
                              : 'bg-white text-amber-700 border border-amber-200'
                          }`}
                        >
                          {month.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm text-amber-800">Días de la semana</Label>
                    <div className="flex flex-wrap gap-1">
                      {days.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleDay(day.value)}
                          className={`px-3 py-1 text-xs rounded-lg transition-all ${
                            formData.double_stamp_days.includes(day.value)
                              ? 'bg-amber-600 text-white'
                              : 'bg-white text-amber-700 border border-amber-200'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  Mejora a plan Growth o superior para configurar días y meses con doble sello para tus clientes.
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 btn-primary"
                data-testid="campaign-submit-btn"
              >
                {editingCampaign ? 'Guardar cambios' : 'Crear campaña'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upgrade Plan Modal */}
      <UpgradePlanModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        title="Límite de campañas alcanzado"
        message={upgradeMessage}
      />

      {/* Voucher Preview Modal */}
      <Dialog open={!!previewCampaign} onOpenChange={() => setPreviewCampaign(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Vista previa del Voucher</DialogTitle>
            <DialogDescription className="text-center">
              Así verá el cliente su voucher cuando complete {previewCampaign?.goal} sellos
            </DialogDescription>
          </DialogHeader>
          
          {previewCampaign && (
            <div className="bg-gradient-to-b from-slate-50 to-white rounded-2xl p-6 border border-slate-200">
              {/* Header */}
              <div className="text-center mb-6">
                <motion.div 
                  className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <Gift className="w-8 h-8 text-primary" />
                </motion.div>
                <h2 className="text-xl font-bold text-slate-900">¡Felicidades!</h2>
                <p className="text-slate-600 text-sm mt-1">{previewCampaign.name}</p>
              </div>

              {/* Reward */}
              <div className="bg-gradient-to-br from-primary to-indigo-600 rounded-xl p-5 text-white text-center mb-5">
                <p className="text-xs text-white/70 mb-1">Tu premio</p>
                <p className="text-xl font-bold">{previewCampaign.reward}</p>
                {previewCampaign.reward_description && (
                  <p className="text-sm text-white/80 mt-2 italic">
                    {previewCampaign.reward_description}
                  </p>
                )}
              </div>

              {/* QR Code Preview */}
              <div className="bg-white rounded-xl p-4 border-2 border-dashed border-slate-200 flex flex-col items-center mb-4">
                <QRCodeSVG 
                  value="VOUCHER-PREVIEW-EXAMPLE" 
                  size={120}
                  level="H"
                />
                <p className="text-xs text-slate-500 mt-3 text-center">
                  Muestra este código en el establecimiento para canjear tu premio
                </p>
              </div>

              {/* Instructions */}
              <div className="p-3 bg-amber-50 rounded-xl">
                <p className="text-xs text-amber-800 text-center">
                  <strong>Importante:</strong> Este voucher solo puede canjearse una vez.
                </p>
              </div>

              {/* Preview Badge */}
              <div className="mt-4 flex justify-center">
                <Badge variant="secondary" className="text-xs">
                  <Eye className="w-3 h-3 mr-1" /> Vista previa
                </Badge>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignsPage;
